import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Search, HelpCircle, Eye, Filter, Upload, Building, MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import { selectCurrentProject, selectCurrentSite, selectInstanceInfo, selectMenuBadges } from '@/entities/layout';
import { sanitizeInput } from '@/lib/security';
import { setSearchQuery, exportConsignments } from '@/store/slices/consignmentSlice';

import { useAppDispatch, usePermissions } from '@/shared/lib';
import { LanguageSelector } from '@/shared/ui';

interface HeaderProps {
  consignmentCount: number;
}

export const Header: React.FC<HeaderProps> = ({ consignmentCount }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchValue, setSearchValue] = useState('');

  const currentProject = useSelector(selectCurrentProject);
  const currentSite = useSelector(selectCurrentSite);
  const instanceInfo = useSelector(selectInstanceInfo);
  const badges = useSelector(selectMenuBadges);
  const { canExportData, isReadOnly } = usePermissions();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeInput(e.target.value);
    setSearchValue(sanitized);
    dispatch(setSearchQuery(sanitized));
  };

  const handleExport = () => {
    dispatch(exportConsignments());
  };

  return (
    <header className="flex w-full justify-between items-center shrink-0 border-b border-border bg-background px-6 py-4 max-lg:flex-col max-lg:gap-4 max-lg:p-4">
      {/* Project and context section */}
      <div className="flex flex-col items-start gap-1 shrink-0 max-lg:w-full">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-foreground text-lg font-semibold leading-tight">
              {currentProject?.name || instanceInfo?.name || 'RITA'}
            </h1>
          </div>
          {currentSite && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span className="text-sm">{currentSite.name}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            {badges && (badges.serviceRequests > 0 || badges.releaseOrders > 0) && (
              <div className="flex gap-1">
                {badges.serviceRequests > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    SRF: {badges.serviceRequests}
                  </Badge>
                )}
                {badges.releaseOrders > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    RO: {badges.releaseOrders}
                  </Badge>
                )}
              </div>
            )}
            {isReadOnly && (
              <Badge variant="outline" className="text-xs border-amber-500 text-amber-700">
                Read Only
              </Badge>
            )}
          </div>
        </div>
        <p className="text-muted-foreground text-xs font-medium">
          {t('navigation.consignments', { count: consignmentCount })}
        </p>
      </div>

      {/* Search input */}
      <div className="flex w-[360px] h-10 items-center gap-2 border-b border-border bg-transparent px-3 max-lg:w-full">
        <Search className="w-4 h-4 text-muted-foreground opacity-50" />
        <Input
          type="text"
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('common.search')}
          className="flex-1 border-none shadow-none bg-transparent text-sm text-muted-foreground placeholder:text-muted-foreground focus-visible:ring-0"
          maxLength={200}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 max-lg:w-full max-lg:justify-between max-sm:flex-wrap">
        <LanguageSelector />

        <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Help">
          <HelpCircle className="w-4 h-4" />
        </Button>

        <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="View">
          <Eye className="w-4 h-4" />
        </Button>

        <Button variant="outline" className="h-9 gap-2">
          <Filter className="w-4 h-4" />
          <span className="max-sm:hidden">{t('common.filters')}</span>
        </Button>

        {canExportData && (
          <Button variant="secondary" className="h-9 gap-2" onClick={handleExport}>
            <Upload className="w-4 h-4" />
            <span className="max-sm:hidden">{t('common.export')}</span>
          </Button>
        )}
      </div>
    </header>
  );
};
